import { Validator as _Validator } from './validator';
import { Behavior } from '@dia-ui/abstract';
import UtilTimer from '@dia-ui/util_timer';

/**
 * based on https://github.com/upjs/facile-validator
 */
@Behavior('validator')
export class Validator {
  static SKIP: boolean = false;

  static forms: any = {};

  element: any;

  validator: _Validator;

  // This is called only once, given the `element` that the behavior is attached to.
  constructor(element) {
    this.element = element;
  }

  // This is called any time the associated `element` is appended into the
  // DOM, passed in the `element`
  async connectedCallback(){
    Validator.resetSkipNext();

    // if element is not a form throw an error
    if (this.element.nodeName !== "FORM") {
        console.error("please use validator on form tag only, rules can be added to fields!");
        return;
    }

    // if form has no name to identify
    if (!this.element.hasAttribute("name")) {
      console.error("please add a name attribute to your form to identify it!");
      return;
    }

    let formName = this.element.getAttribute("name"),
      lang = this.element.ownerDocument.querySelector("html").getAttribute("lang") || "de";

    Validator.forms[formName] = {
      // form instance to validate
      form: this.element,
      // should we validate on field change?
      onFieldChangeValidation: !!this.element.getAttribute("data-validator-change-validation") || true,
      // time to wait till validation on change in milliseconds
      onFieldChangeValidationDelay: Number.parseInt(this.element.getAttribute("data-validator-delay")) || 1000
    };

    // init validator
    await this.initializeValidator(formName, lang);

    // collect initial rules
    let ruleElements = this.element.querySelectorAll("[data-rules]"),
      ruleElementsObject = {};

    ruleElements.forEach(element => {
      ruleElementsObject[element.getAttribute("name")] = element.getAttribute("data-rules");
    });

    // add observer for dynamic changes on rules to update validator instance
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(async (mutation) => {
        let target = mutation.target,
            //@ts-ignore
            targetName = target.getAttribute("name"),
            //@ts-ignore
            targetRules = target.getAttribute("data-rules");

        if (
            !_Validator.PREPROCESSING
            && mutation.type === "attributes"
            && mutation.attributeName === "data-rules"
            && (
              (
                ruleElementsObject[targetName] !== undefined
                && ruleElementsObject[targetName] !== ""
                && ruleElementsObject[targetName] !== targetRules
              )
              || ruleElementsObject[targetName] === undefined
            )
        ) {
          await this.initializeValidator(formName, lang);
        }
      });
    });

    observer.observe(this.element, {
      attributes: true,
      subtree: true
    });
  }

  /**
   * initializeValidator
   * @param formName
   * @param lang
   * @private
   */
  private async initializeValidator(formName: string, lang: string){
    // initialize validator with options
    Validator.forms[formName]['validator'] = new _Validator(this.element, {
      renderErrors: false,
      onFieldChangeValidation: Validator.forms[formName].onFieldChangeValidation,
      onFieldChangeValidationDelay: Validator.forms[formName].onFieldChangeValidationDelay,
      lang: await _Validator.getLang(lang),
    });

    // as you might have guessed, this event will occur when the preprocessing of expression start
    Validator.forms[formName]['validator'].on('validation:preprocess', (form) => {
      Validator.resetFieldErrors(form);
    });

    // as you might have guessed, this event will occur when the validation starts
    Validator.forms[formName]['validator'].on('validation:start', () => {
      //Validator.resetFieldErrors(form);
    });

    // this event will occur when the validation ends, no matter if it was successful or not
    Validator.forms[formName]['validator'].on('validation:end', (form, isSuccessful) => {
      if (isSuccessful) {
        Validator.resetFieldErrors(form);
      }
    });

    // this event will occur when the validation ends with no errors
    Validator.forms[formName]['validator'].on('validation:success', (form) => {
      Validator.resetFieldErrors(form);
    });

    // @ts-ignore this event will occur when the validation ends while there are some errors in the form
    Validator.forms[formName]['validator'].on('validation:failed', (form) => {
      // currently nothing to do here
    });

    // when a particular field has errors, you can handle the errors with this event
    Validator.forms[formName]['validator'].on('field:error', (form, field, errors) => {
      Validator.handleFieldErrors(form, field, errors);
    });

    // validate form on submit
    this.element.addEventListener('submit', (event) => {
      event.preventDefault();

      let formName = this.element.getAttribute("name");

      // Call validate method to start validation
      Validator.validate(formName);
    });
  }

  /**
   * reset
   * @param formName
   */
  public static reset(formName: string) {
    let form = Validator.forms[formName];

    // @ts-ignore
    form.validator.validatorError.clearErrors();

    this.resetFieldErrors(form.form);
  }

  /**
   * handleFieldErrors
   * @param form
   * @param field
   * @param errors
   */
  private static handleFieldErrors(
      // @ts-ignore
      form: HTMLFormElement,
      field: HTMLElement,
      errors: [{
        args: any,
        message: string,
        rule: string,
        element: HTMLElement
      }]
  ){
    let _message = "";

    errors.forEach(error => {
      _message += (!_message ? "" : "\n   ") + "- " + (field.getAttribute("data-validator-message-" + error.rule) || error.message);
    });

    if (_message.length > 0) {
      field.parentElement.setAttribute("data-validator-errors", _message);
    }
  }

  /**
   * resetFieldErrors
   * @param form
   * @private
   */
  private static resetFieldErrors(form: HTMLFormElement) {
    form.querySelectorAll("[data-validator-errors]")
        .forEach(field => field.removeAttribute("data-validator-errors"));
  }

  /**
   * validate
   * @param formName
   * @param delay - milliseconds
   */
  public static async validate(formName: string, delay: number = 0): Promise<boolean> {
    if (Validator.SKIP === true) {
      Validator.SKIP = false;
      return true;
    }

    // this line guarantees that we get all current fields even if its dynamically added
    let validState = await UtilTimer.delay(() => {
      let currentFields = Validator.forms[formName].form.querySelectorAll('[data-rules]');
      return Validator.forms[formName] && Validator.forms[formName]['validator'] ? Validator.forms[formName]['validator'].validate(currentFields) : false;
    }, delay),

    validateEvent= new CustomEvent(formName + ":validate", {
      detail: {
        valid: validState
      }
    });

    window.dispatchEvent(validateEvent);

    // pass all current fields to validator
    return validState;
  }

  /**
   * skipNext
   */
  public static skipNext() {
    Validator.SKIP = true;
  }

  /**
   * resetSkipNext
   */
  public static resetSkipNext() {
    Validator.SKIP = false;
  }

  // This is called any time the associated `element` is removed from the DOM,
  // passed in the `element`.
  disconnectedCallback() {
  }

  // As with custom elements, define which attributes of the associated
  // element that the behavior should react to.
  /*static observedAttributes = [
    'data-draggable-events',
    'data-draggable-selector'
  ];*/

  // This is called any time any of the `observedAttributes` of the associated
  // element have been changed, just like with Custom Elements but with the
  // additional passed in `element`.
  // attributeChangedCallback(attributeName, oldValue, newValue, element) {}

  // There is one additional API, unlike with Custom Elements. If `static
  // awaitElementDefined` is `true`, then the behavior will not be
  // instantiated and connected until its host element is defined and upgraded
  // (that is, if the host element is possibly a custom element, having a
  // hyphen in its name). If the host element has no hyphen in its name, then
  // this does not apply, and the behavior will be created and connected
  // immediately without waiting. If a possibly-custom element is removed
  // before it is defined, then a behavior will not be created and connected
  // at all (waiting will have been canceled).
  static awaitElementDefined = true // Default is false.
}

/**
 * ValidateBefore
 * @param formName
 * @param delay
 * @constructor
 */
export const ValidateBefore = (formName: string, delay: number = 0) => {
  // @ts-ignore
  return (proto: any, method: string, descriptor: PropertyDescriptor) => {
    const original: (...args: Array<unknown>) => unknown = descriptor.value;

    descriptor.value = async function (...args: Array<unknown>): Promise<unknown> {
      if (!Validator.forms[formName]) {
        console.error("form with name '" + formName + "' not found by validate decorator!");
      }

      let validate = await Validator.validate(formName, delay);
      if (!validate) return;

      return await original.apply(this, args);
    };

    return descriptor;
  };
}

/**
 * ValidateAfter
 * @param formName
 * @constructor
 */
export const ValidateAfter = (formName: string) => {
  // @ts-ignore
  return (proto: any, method: string, descriptor: PropertyDescriptor) => {
    const original: (...args: Array<unknown>) => unknown = descriptor.value;

    descriptor.value = async function(...args: Array<unknown>): Promise<unknown> {
      if (!Validator.forms[formName]) {
        console.error("form with name '" + formName + "' not found by validate decorator!");
      }

      let value = original.apply(this, args)

      await Validator.validate(formName);

      return value;
    };
  };
}
